import { FitType } from '../components/tables/common-individual-report-table/IFormInteractionHandler';

export const FORM_ZOOM_SESSION_STORAGE_KEY = 'comet-review-form-zoom-settings';

export interface FormSettings {
    currentZoom: number;
    zoomFitType: FitType;
}

export const DEFAULT_FORM_SETTINGS: FormSettings = {
    currentZoom: 100,
    zoomFitType: 'fitToWidth',
};

export interface PdfMagnificationLike {
    fitToPage(): void;
    fitToWidth(): void;
    fitToAuto(): void;
    zoomTo(zoomPercentage: number): void;
}

const VALID_FIT_TYPES: FitType[] = ['fitToPage', 'fitToWidth', 'fitToAuto', null];

export const normalizeFormSettings = (value: unknown): FormSettings => {
    if (!value || typeof value !== 'object') {
        return { ...DEFAULT_FORM_SETTINGS };
    }

    const candidate = value as Partial<FormSettings>;
    const currentZoom = typeof candidate.currentZoom === 'number' && Number.isFinite(candidate.currentZoom) ? candidate.currentZoom : 100;
    const zoomFitType =
        candidate.zoomFitType === undefined || !VALID_FIT_TYPES.includes(candidate.zoomFitType)
            ? DEFAULT_FORM_SETTINGS.zoomFitType
            : candidate.zoomFitType;

    return {
        currentZoom,
        zoomFitType,
    };
};

export const readFormSettingsFromSession = (storage: Storage = localStorage): FormSettings => {
    try {
        const raw = storage.getItem(FORM_ZOOM_SESSION_STORAGE_KEY);
        if (!raw) {
            return { ...DEFAULT_FORM_SETTINGS };
        }

        return normalizeFormSettings(JSON.parse(raw));
    } catch {
        return { ...DEFAULT_FORM_SETTINGS };
    }
};

export const writeFormSettingsToSession = (settings: FormSettings, storage: Storage = localStorage): void => {
    storage.setItem(FORM_ZOOM_SESSION_STORAGE_KEY, JSON.stringify(settings));
};

export const clearFormZoomSettingsFromSession = (storage: Storage = localStorage): void => {
    storage.removeItem(FORM_ZOOM_SESSION_STORAGE_KEY);
};

export const applyFormZoomSettings = (magnification: PdfMagnificationLike, settings: FormSettings): void => {
    switch (settings.zoomFitType) {
        case 'fitToPage':
            magnification.fitToPage();
            break;
        case 'fitToWidth':
            magnification.fitToWidth();
            break;
        case 'fitToAuto':
            magnification.fitToAuto();
            break;
        default:
            magnification.zoomTo(settings.currentZoom);
            break;
    }
};

export const createPercentageZoomSettings = (zoomPercentage: number): FormSettings => ({
    currentZoom: zoomPercentage,
    zoomFitType: null,
});

export const createFitTypeZoomSettings = (zoomFitType: Exclude<FitType, null>, currentZoom?: number): FormSettings => ({
    currentZoom: currentZoom ?? DEFAULT_FORM_SETTINGS.currentZoom,
    zoomFitType,
});
